import { gql } from '@apollo/client';
import { RotateCw } from 'lucide-react';
import * as React from 'react';
import { Button } from 'src/shadcn/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shadcn/components/ui/dialog';
import { useHotToast } from 'src/utils/useHotToast';
import {
  useGetCurrentShiftForLoginQuery,
  useStartShiftMutation,
} from './__generated__/PostEmployeeLoginDialog.generated';
import { useAuthContext } from 'src/Components/AuthContext';
import { AuthTokenType } from 'src/graphql/__generated__/globalTypes';
import { ManageShiftComponent } from 'src/Components/ManageShift/ManageShiftComponent';

export interface IPostEmployeeLoginDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onComplete: () => void;
}

export const PostEmployeeLoginDialog: React.FunctionComponent<
  IPostEmployeeLoginDialogProps
> = props => {
  const authContext = useAuthContext();
  if (authContext.user?.authTokenType === AuthTokenType.MECHANIC) {
    return <ManageShiftComponent onClose={props.onComplete} />;
  }
  const { data, isGettingShift } = useGetCurrentShift();
  const { isStartingShift, startShift } = useStartShift();
  React.useEffect(() => {
    if (data?.getCurrentShift) {
      props.onComplete();
    }
  }, [data]);
  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={newOpen => {
        props.onOpenChange(newOpen);
        if (!newOpen) {
          props.onComplete();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isGettingShift ? 'Logging you in...' : 'Starting your shift?'}
          </DialogTitle>
          <DialogDescription>Start your shift right now. </DialogDescription>
        </DialogHeader>
        {!isGettingShift && (
          <DialogFooter className='items-center'>
            <Button
              disabled={isStartingShift}
              onClick={() => {
                startShift(props.onComplete);
              }}
            >
              <span>Start shift & login</span>
              {isStartingShift && (
                <RotateCw className='ml-2 h-4 w-4 animate-spin' />
              )}
            </Button>
            <Button
              variant={'secondary'}
              disabled={isStartingShift}
              onClick={() => {
                props.onComplete();
              }}
            >
              <span>Login without starting shift</span>
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

function useStartShift() {
  const toast = useHotToast();
  const [startShiftMutation, { loading: isStartingShift }] =
    useStartShiftMutation({
      onError: e => {
        toast.error(e.message);
      },
    });
  return {
    isStartingShift,
    startShift: (onShiftStarted: () => void) => {
      startShiftMutation({
        onCompleted: onShiftStarted,
      });
    },
  };
}

const query = gql`
  mutation StartShift {
    employeeStartShift {
      id
      startTime
    }
  }
`;

function useGetCurrentShift() {
  const toast = useHotToast();
  const authContext = useAuthContext();
  const { data, loading } = useGetCurrentShiftForLoginQuery({
    fetchPolicy: 'network-only',
    onError: error => {
      toast.error('Could not get your shift.');
    },
    skip: !Boolean(authContext.user?.employee?.id),
  });
  return {
    data,
    isGettingShift: loading,
  };
}

const query2 = gql`
  query GetCurrentShiftForLogin {
    getCurrentShift {
      id
      startTime
      endTime
    }
  }
`;
